import apiClient from './apiClient';

export interface LunchRoulette {
  Roulette_ID: string;
  group_size: number;
  registration_start: string;
  registration_end: string;
  lunch_date: string;
  confirmed: boolean;
  is_hybrid: boolean;
  not_same_department: boolean;
  not_same_employee_group: boolean;
  registration_status: 'not_started' | 'open' | 'closed';
  is_registered: boolean;
  has_matches: boolean;
}

export interface LunchRouletteRegistration {
  User_ID: string;
  Roulette_ID: string;
  registered_at: string;
  comment?: string;
}

export interface LunchRouletteOverview {
  past: LunchRoulette[];
  upcoming: LunchRoulette[];
  user_registrations: LunchRouletteRegistration[];
  all_registrations: LunchRouletteRegistration[];
  user_permissions: string[];
}

export interface LunchRouletteGroup {
  Group_ID: string;
  Roulette_ID: string;
  created_at: string;
  mail_sent?: boolean;
  members: Array<{
    User_ID: string;
    firstname: string;
    lastname: string;
    mail: string;
    jobtitle?: string;
    department?: string;
    employee_group?: string;
    comment?: string;
    picture?: string;
    city_name?: string;
    registered_at: string;
  }>;
}

export interface UnmatchedUser {
  User_ID: string;
  firstname: string;
  lastname: string;
  mail: string;
  jobtitle?: string;
  department?: string;
  employee_group?: string;
  comment?: string;
  picture?: string;
  city_name?: string;
  registered_at: string;
}

export interface MailLogData {
  Mail_Log_ID: string;
  sent_at: string;
  total_groups: number;
  total_recipients: number;
  sent_by: string;
}

export interface AdminPanelData {
  roulette: LunchRoulette;
  matches: LunchRouletteGroup[];
  unmatched: UnmatchedUser[];
  registered_count: number;
  mail_log?: MailLogData | null;
  available_users?: Array<{
    User_ID: string;
    firstname: string;
    lastname: string;
    mail: string;
    jobtitle?: string;
    department?: string;
    employee_group?: string;
    picture?: string;
    city_name?: string;
  }>;
}

export interface CreateLunchRouletteRequest {
  group_size: number;
  registration_start: string;
  registration_end: string;
  lunch_date: string;
  is_hybrid: boolean;
  not_same_department: boolean;
  not_same_employee_group: boolean;
}

export interface CreateGroupRequest {
  selected_users: string[];
}

export interface AddMemberRequest {
  Group_ID: string;
  selected_users: string[];
}

export interface AddMembersRequest {
  selected_users: string[];
  group_id: string;
}

export interface LunchRouletteUserMatch {
  roulette: LunchRoulette;
  is_registered: boolean;
  my_match_group?: LunchRouletteGroup;
  user_id: string;
}

export const lunchRouletteApi = {
  /**
   * Get overview of all lunch roulettes with registration data
   */
  getOverview: async (): Promise<LunchRouletteOverview> => {
    try {
      console.log('Fetching lunch roulette overview...');
      const response = await apiClient.get<LunchRouletteOverview>('/api/v2/web/lunch-roulette/overview/');
      console.log('Lunch roulette overview response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getOverview:', error);
      throw error;
    }
  },

  /**
   * Register for a lunch roulette
   */
  register: async (rouletteId: string, comment?: string): Promise<{ message: string }> => {
    try {
      console.log('Registering for lunch roulette:', rouletteId);
      const response = await apiClient.post<{ message: string }>(
        `/api/v2/web/lunch-roulette/register/${rouletteId}`,
        { comment }
      );
      console.log('Registration response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in register:', error);
      throw error;
    }
  },

  /**
   * Unregister from a lunch roulette
   */
  unregister: async (rouletteId: string): Promise<{ message: string }> => {
    try {
      console.log('Unregistering from lunch roulette:', rouletteId);
      const response = await apiClient.delete<{ message: string }>(
        `/api/v2/web/lunch-roulette/unregister/${rouletteId}`
      );
      console.log('Unregistration response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in unregister:', error);
      throw error;
    }
  },

  /**
   * Get admin panel data for a specific lunch roulette
   */
  getAdminPanelData: async (rouletteId: string): Promise<AdminPanelData> => {
    try {
      console.log('Fetching admin panel data for roulette:', rouletteId);
      
      // Fetch admin panel data and available users in parallel
      const [adminDataResponse, availableUsersResponse] = await Promise.all([
        apiClient.get<Omit<AdminPanelData, 'available_users'>>(
          `/api/v2/web/lunch-roulette/admin-panel/${rouletteId}`
        ),
        apiClient.get<Array<{
          User_ID: string;
          firstname: string;
          lastname: string;
          mail: string;
          jobtitle?: string;
          department?: string;
          employee_group?: string;
          picture?: string;
          city_name?: string;
        }>>(
          `/api/v2/web/lunch-roulette/admin-panel/${rouletteId}/available-users`
        )
      ]);
      
      const result: AdminPanelData = {
        ...adminDataResponse.data,
        available_users: availableUsersResponse.data
      };
      
      console.log('Admin panel data response:', result);
      return result;
    } catch (error) {
      console.error('Error in getAdminPanelData:', error);
      throw error;
    }
  },

  /**
   * Create a new lunch roulette (Admin only)
   */
  create: async (data: CreateLunchRouletteRequest): Promise<{ message: string; Roulette_ID: string }> => {
    try {
      console.log('Creating lunch roulette:', data);
      const response = await apiClient.post<{ message: string; Roulette_ID: string }>(
        '/api/v2/web/lunch-roulette/create/',
        data
      );
      console.log('Create lunch roulette response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in create:', error);
      throw error;
    }
  },

  /**
   * Generate matches for a lunch roulette (Admin only)
   */
  generateMatches: async (rouletteId: string): Promise<{ message: string; groups: Array<{ group_size: number; members: string[] }>; unmatched: number }> => {
    try {
      console.log('Generating matches for roulette:', rouletteId);
      const response = await apiClient.post<{ message: string; groups: Array<{ group_size: number; members: string[] }>; unmatched: number }>(
        `/api/v2/web/lunch-roulette/generate-matches/${rouletteId}`
      );
      console.log('Generate matches response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in generateMatches:', error);
      throw error;
    }
  },

  /**
   * Create a new group with selected users (Admin only)
   */
  createGroup: async (rouletteId: string, data: CreateGroupRequest): Promise<{ message: string; Group_ID: string }> => {
    try {
      console.log('Creating group for roulette:', rouletteId, data);
      const response = await apiClient.post<{ message: string; Group_ID: string }>(
        `/api/v2/web/lunch-roulette/create-group/${rouletteId}`,
        data
      );
      console.log('Create group response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in createGroup:', error);
      throw error;
    }
  },

  /**
   * Add members to an existing group (Admin only)
   */
  addMembersToGroup: async (rouletteId: string, data: AddMemberRequest): Promise<{ message: string }> => {
    try {
      console.log('Adding members to group for roulette:', rouletteId, data);
      const response = await apiClient.post<{ message: string }>(
        `/api/v2/web/lunch-roulette/add-members/${rouletteId}`,
        data
      );
      console.log('Add members response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in addMembersToGroup:', error);
      throw error;
    }
  },

  /**
   * Remove a member from a group (Admin only)
   */
  removeMemberFromGroup: async (rouletteId: string, groupId: string, userId: string): Promise<{ message: string }> => {
    try {
      console.log('Removing member from group:', rouletteId, groupId, userId);
      const response = await apiClient.delete<{ message: string }>(
        `/api/v2/web/lunch-roulette/remove-member/${rouletteId}/${groupId}/${userId}`
      );
      console.log('Remove member response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in removeMemberFromGroup:', error);
      throw error;
    }
  },

  /**
   * Delete a group (Admin only)
   */
  deleteGroup: async (rouletteId: string, groupId: string): Promise<{ message: string }> => {
    try {
      console.log('Deleting group:', rouletteId, groupId);
      const response = await apiClient.delete<{ message: string }>(
        `/api/v2/web/lunch-roulette/delete-group/${rouletteId}/${groupId}`
      );
      console.log('Delete group response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in deleteGroup:', error);
      throw error;
    }
  },

  /**
   * Export lunch roulette data to Excel (Admin only)
   */
  exportToExcel: async (rouletteId: string): Promise<Blob> => {
    try {
      console.log('Exporting lunch roulette to Excel:', rouletteId);
      const response = await apiClient.get(
        `/api/v2/web/lunch-roulette/export/${rouletteId}`,
        {
          responseType: 'blob',
          headers: {
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error in exportToExcel:', error);
      throw error;
    }
  },

  /**
   * Get direct registration link
   */
  getDirectRegisterLink: (rouletteId: string): string => {
    return `${window.location.origin}/lunch-roulette/?open_register=${rouletteId}`;
  },

  /**
   * Get user's matches for a specific lunch roulette
   */
  getUserMatches: async (rouletteId: string): Promise<LunchRouletteUserMatch> => {
    try {
      console.log('Fetching user matches for roulette:', rouletteId);
      const response = await apiClient.get<LunchRouletteUserMatch>(
        `/api/v2/web/lunch-roulette/my-matches/${rouletteId}`
      );
      console.log('User matches response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getUserMatches:', error);
      throw error;
    }
  },

  /**
   * Register a user for a lunch roulette (Admin only)
   */
  adminRegisterUser: async (rouletteId: string, userId: string): Promise<{ message: string }> => {
    try {
      console.log('Admin registering user:', userId, 'for roulette:', rouletteId);
      const response = await apiClient.post<{ message: string }>(
        `/api/v2/web/lunch-roulette/admin-register/${rouletteId}/${userId}`
      );
      console.log('Admin register response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in adminRegisterUser:', error);
      throw error;
    }
  },

  /**
   * Unregister a user from a lunch roulette (Admin only)
   */
  adminUnregisterUser: async (rouletteId: string, userId: string): Promise<{ message: string }> => {
    try {
      console.log('Admin unregistering user:', userId, 'from roulette:', rouletteId);
      const response = await apiClient.delete<{ message: string }>(
        `/api/v2/web/lunch-roulette/admin-unregister/${rouletteId}/${userId}`
      );
      console.log('Admin unregister response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in adminUnregisterUser:', error);
      throw error;
    }
  },

  /**
   * Send match notification emails to all participants (Admin only)
   */
  sendMatches: async (rouletteId: string): Promise<{ message: string; total_recipients: number; total_groups: number; failed_sends: number; mail_log_id: string }> => {
    try {
      console.log('Sending match emails for roulette:', rouletteId);
      const response = await apiClient.post<{ message: string; total_recipients: number; total_groups: number; failed_sends: number; mail_log_id: string }>(
        `/api/v2/web/lunch-roulette/send-matches/${rouletteId}`
      );
      console.log('Send matches response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in sendMatches:', error);
      throw error;
    }
  },

  /**
   * Send match notification emails to a specific group (Admin only)
   */
  sendGroupMatches: async (rouletteId: string, groupId: string): Promise<{ message: string; total_recipients: number; failed_sends: number; mail_log_id: string }> => {
    try {
      console.log('Sending match emails for group:', groupId, 'in roulette:', rouletteId);
      const response = await apiClient.post<{ message: string; total_recipients: number; failed_sends: number; mail_log_id: string }>(
        `/api/v2/web/lunch-roulette/send-group-matches/${rouletteId}/${groupId}`
      );
      console.log('Send group matches response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in sendGroupMatches:', error);
      throw error;
    }
  },

  /**
   * Download invitation email as HTML file (Admin only)
   */
  downloadInvitation: async (rouletteId: string, additionalInfoDe?: string, additionalInfoEn?: string): Promise<Blob> => {
    try {
      console.log('Downloading invitation for roulette:', rouletteId);
      const response = await apiClient.post(
        `/api/v2/web/lunch-roulette/download-invitation/${rouletteId}`,
        {
          additional_info_de: additionalInfoDe || "",
          additional_info_en: additionalInfoEn || ""
        },
        {
          responseType: 'blob',
          headers: {
            'Accept': 'text/html'
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error in downloadInvitation:', error);
      throw error;
    }
  },
};
